/* eslint-disable no-undef */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import JitsiMeetJS from 'store/modules/base/lib-jitsi-meet/_';
import { leaveConference, reloadNow } from 'store/modules/app/actions';
import { updateSleepSettings } from 'store/modules/settings/actions';
import { setCameraLostOverlay } from 'store/modules/overlay/actions';
import logger from 'store/modules/app/logger';

/**
 * Implements an abstract React {@link Component} for the page reload overlays.
 *
 * FIXME: This is not really an abstract class as some components and functions are very web specific.
 */
export default class AbstractCameraLostOverlay extends Component {
  /**
   * Determines whether this overlay needs to be rendered (according to a
   * specific redux state). Called by {@link OverlayContainer}.
   *
   * @param {Object} state - The redux state.
   * @returns {boolean} - If this overlay needs to be rendered, {@code true};
   * {@code false}, otherwise.
   */
  static needsRender(state) {
    return state['features/overlay'].cameraLostOverlayVisible;
  }

  /**
   * Initializes a new AbstractCameraLostOverlay instance.
   *
   * @param {Object} props - The read-only properties with which the new
   * instance is to be initialized.
   * @public
   */
  constructor(props) {
    super(props);

    this.state = {
      title: 'dialog.cameraNotSendingDataTitle',
      message: 'dialog.cameraNotSendingData',
      timeoutSeconds: 15,
      timeLeft: 0
    };
  }

  /**
   * React Component method that executes once component is mounted.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentDidMount() {
    this.props.dispatch(leaveConference());
    if (!this.props.userIdleMode) {
      this.props.dispatch(updateSleepSettings({ userIdleMode: true }));
    }

    if (this.props.hasVideoPermission) {
      logger.info(
        'Device has camera permission, testing whether a local track is bulidable'
      );
      this._startTimer();
    } else {
      logger.info('Device appears to have lost its camera permissions');
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.hasVideoPermission && this.props.hasVideoPermission) {
      logger.info('Camera permission granted, reloading now');
      if (!this._autoTestInterval) {
        this._startTimer();
      }
    }
  }

  componentWillUnmount() {
    if (this._autoTestInterval) {
      clearInterval(this._autoTestInterval);
      this._autoTestInterval = undefined;
    }
    if (this._idleTimeout) {
      clearTimeout(this._idleTimeout);
      this._idleTimeout = undefined;
    }
    this.props.dispatch(reloadNow());
  }

  startIdleTimer() {
    if (this._idleTimeout) {
      clearTimeout(this._idleTimeout);
    }

    this.props.dispatch(updateSleepSettings({ userIdleMode: false }));
    this._idleTimeout = setTimeout(() => {
      this.props.dispatch(updateSleepSettings({ userIdleMode: true }));
      this._idleTimeout = undefined;
    }, 10000);
  }

  _startTimer = (seconds = 15) => {
    this.setState({ timeoutSeconds: seconds, timeLeft: seconds });
    this._autoTestInterval = setInterval(() => {
      if (this.state.timeLeft === 0) {
        if (this._autoTestInterval) {
          clearInterval(this._autoTestInterval);
          this._autoTestInterval = undefined;
        }
        this._runAutoVideoTest();
      } else {
        this.setState((prevState) => {
          return {
            timeLeft: prevState.timeLeft - 1
          };
        });
      }
    }, 1000);
  };

  _runAutoVideoTest = async () => {
    let createdTrack;
    try {
      const tracks = await JitsiMeetJS.createLocalTracks({
        devices: ['video']
      });
      [createdTrack] = tracks;
      if (createdTrack) {
        this.props.dispatch(setCameraLostOverlay(false));
      } else {
        this._startTimer(30);
      }
    } catch (err) {
      // If this becomes noisy, consider rate limiting/downgrading
      // after the first failure.
      logger.warn('Failed to create a local track during video test', err);
      this._startTimer(30);
    } finally {
      if (createdTrack?.dispose) {
        createdTrack.dispose();
      }
    }
  };

  _requestPermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      stream.getTracks().forEach((track) => track.stop());
      this.setState({
        lastPermissionResult: {
          success: true,
          message: 'Permission granted, reloading...'
        }
      });
    } catch (err) {
      this.setState({
        lastPermissionResult: {
          success: false,
          message: err?.message || String(err)
        }
      });
      console.warn('Camera permission denied or unavailable', err);
    }
  };

  /**
   * Renders the camera permission display and request button.
   *
   * @protected
   * @returns {ReactElement}
   */
  _renderCameraPermissions() {
    return (
      <div>
        {(!this.props.hasVideoPermission || !this.props.hasAudioPermission) && (
          <div className="permission_button_row">
            <button
              className="permission_button"
              onClick={this._requestPermissions}>
              Request Permissions
            </button>
          </div>
        )}
        {this.state.lastPermissionResult && (
          <div
            className={`permission_result ${
              this.state.lastPermissionResult.success
                ? 'permission_success'
                : 'permission_failure'
            }`}>
            {this.state.lastPermissionResult.message}
            {!this.state.lastPermissionResult.success &&
              ', please try again or reload.'}
          </div>
        )}
        <div className="permission_status">
          You currently {this.props.hasVideoPermission ? '' : 'do not'} have
          camera permissions enabled.
        </div>
        <div className="permission_status">
          You currently {this.props.hasAudioPermission ? '' : 'do not'} have
          audio permissions enabled.
        </div>
      </div>
    );
  }

  /**
   * Renders the progress bar.
   *
   * @protected
   * @returns {ReactElement}
   */
  _renderProgressBar() {
    const { timeLeft, timeoutSeconds } = this.state;
    const timeRemaining = timeoutSeconds - timeLeft;
    const percentageComplete = Math.floor(
      (timeRemaining / timeoutSeconds) * 100
    );

    return (
      <div className="progress-indicator" id="reloadProgressBar">
        <div
          className="progress-indicator-fill"
          style={{ width: `${percentageComplete}%` }}
        />
      </div>
    );
  }

  /**
   * Renders the button for reloading the page if necessary.
   *
   * @protected
   * @returns {ReactElement}
   */
  _renderReloadButton() {
    return (
      <button
        className="button-control button-control_overlay button-control_center'"
        onClick={() => {
          this.props.dispatch(setCameraLostOverlay(false));
        }}>
        {this.props.t('dialog.rejoinNow')}
      </button>
    );
  }
}

/**
 * The type of the React {@code Component} props of
 * {@link AbstractCameraLostOverlay}.
 */
AbstractCameraLostOverlay.propTypes = {
  dispatch: PropTypes.func,

  /**
   * The function to translate human-readable text.
   */
  t: PropTypes.func,
  hasAudioPermission: PropTypes.bool,
  hasVideoPermission: PropTypes.bool,
  userIdleMode: PropTypes.bool,
  visible: PropTypes.bool
};

/**
 * Maps (parts of) the redux state to the associated component's props.
 *
 * @param {Object} state - The redux state.
 * @protected
 * @returns {{
 *     hasAudioPermission: boolean,
 *     hasVideoPermission: boolean
 * }}
 */
export function abstractMapStateToProps(state) {
  const { permissions } = state['features/base/devices'];
  const userIdleMode = state['features/settings'].userIdleMode;
  const userDevice = state['features/login'].user?.device;

  return {
    hasAudioPermission: permissions.audio,
    hasVideoPermission: permissions.video,
    userIdleMode,
    visible: !(userIdleMode && userDevice?.options?.hideOtherText === 1)
  };
}
